import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useTranslation from '@/hooks/useTranslation'
import useCart from '@/hooks/useCart'
import usePageTitle from '@/hooks/usePageTitle'
import useRetailerApi from '@/useRetailerApi'
import { LANGUAGE_LOCALES } from '@/locales/languages'
import OptimizedImage from '@/components/OptimizedImage'
import ProductModal from '@/components/ProductModal'
import ResponsiveModal from '@/components/ResponsiveModal'
import Button from '@/components/Button'

function Cart() {
    const { t, language } = useTranslation()
    usePageTitle(t('nav.cart'))
    const { cartItems, cartCount, cartSubtotal, clearCart, updateQuantity, removeFromCart, canAddToProduct } = useCart()
    const { retailer, submitEstimate, fetchShippingCost } = useRetailerApi()
    const [shippingCost, setShippingCost] = useState(null)
    const [shippingLoading, setShippingLoading] = useState(false)
    const hasVat = retailer?.billing_country_code === 'FR'
    const shippingAmount = shippingCost ?? 0
    const vatAmount = hasVat ? (cartSubtotal + shippingAmount) * 0.2 : 0
    const cartTotal = cartSubtotal + shippingAmount + vatAmount
    const navigate = useNavigate()
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState(null)
    const [modalProduct, setModalProduct] = useState(null)
    const [modalVariantId, setModalVariantId] = useState(null)
    const [confirmOpen, setConfirmOpen] = useState(false)

    useEffect(() => {
        if (cartItems.length === 0 || retailer?.has_free_shipping) {
            setShippingCost(retailer?.has_free_shipping ? 0 : null)
            return
        }

        setShippingLoading(true)
        const lines = cartItems.map((item) => ({
            variant_id: item.variantId,
            quantity: item.quantity,
        }))
        fetchShippingCost(lines)
            .then((data) => setShippingCost(data.shipping_cost))
            .catch(() => setShippingCost(null))
            .finally(() => setShippingLoading(false))
    }, [cartItems, retailer?.has_free_shipping])

    const { productsByVariantId, variantById } = useMemo(() => {
        const products = {}
        const variants = {}
        for (const collection of retailer?.collections ?? []) {
            for (const product of collection.products) {
                for (const variant of product.variants) {
                    products[variant.id] = product
                    variants[variant.id] = variant
                }
            }
        }
        return { productsByVariantId: products, variantById: variants }
    }, [retailer?.collections])

    // Group cart items by productTitle, compute group total price
    const groupedItems = useMemo(() => {
        const groups = []
        const seen = new Map()
        for (const item of cartItems) {
            if (!seen.has(item.productTitle)) {
                seen.set(item.productTitle, groups.length)
                groups.push({
                    productTitle: item.productTitle,
                    imageUrl: item.imageUrl,
                    items: [],
                })
            }
            const group = groups[seen.get(item.productTitle)]
            group.items.push(item)
        }
        // Compute group total
        for (const group of groups) {
            group.total = group.items.reduce(
                (sum, i) => sum + i.price * i.quantity,
                0
            )
            // Use the first item's image as group image
            group.imageUrl = group.items[0]?.imageUrl
        }
        groups.sort((a, b) => a.productTitle.localeCompare(b.productTitle))
        return groups
    }, [cartItems])

    const formatPrice = (amount) =>
        amount.toLocaleString(LANGUAGE_LOCALES[language], {
            style: 'currency',
            currency: 'EUR',
        })

    const handleEdit = (item) => {
        const product = productsByVariantId[item.variantId]
        if (product) {
            setModalProduct(product)
            setModalVariantId(item.variantId)
        }
    }

    const handleSubmit = () => {
        setConfirmOpen(true)
    }

    const handleConfirm = async () => {
        setSubmitting(true)
        setError(null)
        try {
            const lines = cartItems.map((item) => ({
                variant_id: item.variantId,
                quantity: item.quantity,
            }))
            await submitEstimate(lines)
            clearCart()
            navigate('/orders', { state: { fromCartSubmit: true } })
        } catch {
            setError(t('cart.submitError'))
            setConfirmOpen(false)
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="flex flex-col px-[24px] lg:px-[48px] py-[100px] lg:py-[172px] mx-auto">
            <div className="flex flex-col gap-[4px] mb-[40px]">
                <h1 className="text-[34px] lg:text-[40px] font-semibold uppercase">
                    {t('nav.cart')}
                </h1>
                <span className="uppercase text-dark-blue-40 text-[14px]">
                    {t('cart.productCount', { count: cartCount })}
                </span>
            </div>

            {cartItems.length === 0 ? (
                <p className="text-dark-blue-40 uppercase text-[14px]">
                    {t('cart.empty')}
                </p>
            ) : (
                <div className="flex flex-col lg:flex-row gap-[80px]">
                    {/* Product list */}
                    <div className="flex-1 flex flex-col">
                        {groupedItems.map((group, groupIndex) => (
                            <div
                                key={group.productTitle}
                                className={`flex flex-col lg:flex-row items-start gap-[20px] py-[20px] ${
                                    groupIndex > 0
                                        ? 'border-t border-dark-blue/10'
                                        : ''
                                }`}
                            >
                                {/* Product image */}
                                <div className="w-[135px] h-[180px] bg-background-product rounded-[4px] flex items-center justify-center p-[20px] shrink-0">
                                    <OptimizedImage
                                        className="w-[91px] h-[135px] object-contain"
                                        src={group.imageUrl}
                                        alt={group.productTitle}
                                        sizes="91px"
                                        widths={[91, 182]}
                                    />
                                </div>

                                {/* Product info */}
                                <div className="flex-1 flex flex-col gap-[12px]">
                                    {/* Header: title + price */}
                                    <div className="flex justify-between items-start gap-[20px]">
                                        <span className="text-[16px] uppercase">
                                            {group.productTitle}
                                        </span>
                                        <span className="text-[16px] font-semibold uppercase shrink-0">
                                            {formatPrice(group.total)}
                                        </span>
                                    </div>

                                    {/* Variant lines */}
                                    <div className="flex flex-col gap-[4px]">
                                        {group.items.map((item) => {
                                            const variant = variantById[item.variantId]
                                            const withinProductLimit = canAddToProduct(item.productTitle)
                                            const withinStockLimit = variant?.stock_status === 'low' && variant?.stock_quantity != null ? item.quantity < variant.stock_quantity : true
                                            const atMax = !withinProductLimit || !withinStockLimit
                                            return (
                                                <div
                                                    key={item.variantId}
                                                    className="flex items-center gap-[12px]"
                                                >
                                                    <div className="flex items-center h-[28px] border border-dark-blue/10 rounded-full px-px gap-[4px] shrink-0 select-none">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (item.quantity <= 1) {
                                                                    removeFromCart(item.variantId)
                                                                } else {
                                                                    updateQuantity(item.variantId, item.quantity - 1)
                                                                }
                                                            }}
                                                            className="w-[26px] h-[26px] flex items-center justify-center rounded-full cursor-pointer hover:bg-dark-blue/4 transition-colors"
                                                        >
                                                            <span className="block w-[10px] h-0 border-t border-dark-blue/20" />
                                                        </button>
                                                        <span className="text-[14px] text-center leading-none min-w-[10px] font-mono tabular-nums">
                                                            {item.quantity}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            disabled={atMax}
                                                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                                            className={`w-[26px] h-[26px] flex items-center justify-center rounded-full relative transition-colors ${atMax ? 'opacity-20 cursor-default' : 'cursor-pointer hover:bg-dark-blue/4'}`}
                                                        >
                                                            <span className="block w-[10px] h-0 border-t border-dark-blue absolute" />
                                                            <span className="block w-0 h-[10px] border-l border-dark-blue absolute" />
                                                        </button>
                                                    </div>
                                                    <span className="text-[16px] leading-none">
                                                        {item.variantTitle === 'Default Title'
                                                            ? item.productTitle
                                                            : item.options
                                                                ?.map((o) => o.value)
                                                                .join(' - ') || item.variantTitle}
                                                    </span>
                                                    {item.sku && (
                                                        <span className="text-[14px] leading-none text-dark-blue-40">
                                                            {item.sku}
                                                        </span>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>

                                    <button
                                        onClick={() => handleEdit(group.items[0])}
                                        className="text-[14px] underline cursor-pointer self-start"
                                    >
                                        {t('cart.edit')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Sidebar */}
                    <div className="lg:w-[440px] shrink-0">
                        <div className="flex flex-col gap-[24px] lg:sticky lg:top-[120px]">
                            <div className="bg-background-product rounded-[8px] p-[40px] flex flex-col gap-[40px]">
                                <h2 className="text-[20px] font-semibold uppercase">
                                    {t('cart.summary')}
                                </h2>

                                <div className="flex flex-col gap-[16px]">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[16px]">
                                            {t('cart.subtotal')}
                                        </span>
                                        <span className="text-[16px]">
                                            {formatPrice(cartSubtotal)}
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[16px]">
                                            {t('cart.shipping')}
                                        </span>
                                        <span className="text-[16px]">
                                            {retailer?.has_free_shipping
                                                ? t('cart.freeShipping')
                                                : shippingLoading
                                                  ? '...'
                                                  : shippingCost !== null
                                                    ? formatPrice(shippingCost)
                                                    : '—'}
                                        </span>
                                    </div>
                                    {hasVat && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-[16px]">
                                                {t('cart.vat')}
                                            </span>
                                            <span className="text-[16px]">
                                                {formatPrice(vatAmount)}
                                            </span>
                                        </div>
                                    )}

                                    <div className="border-t border-dark-blue/10" />

                                    <div className="flex justify-between items-center">
                                        <span className="text-[16px] font-semibold">
                                            {hasVat ? t('cart.totalTTC') : t('cart.total')}
                                        </span>
                                        <span className="text-[16px] font-semibold">
                                            {formatPrice(cartTotal)}
                                        </span>
                                    </div>
                                </div>

                                {error && (
                                    <p className="text-red-500 text-[13px]">
                                        {error}
                                    </p>
                                )}

                                {!retailer?.can_order && (
                                    <p className="text-red-500 text-[13px]">
                                        {t('cart.cannotOrder')}
                                    </p>
                                )}

                                {cartCount < 3 && (
                                    <p className="text-dark-blue-40 text-[13px]">
                                        {t('cart.minimumOrder')}
                                    </p>
                                )}

                                <Button
                                    fullWidth
                                    loading={submitting}
                                    disabled={!retailer?.can_order || cartCount < 3 || shippingLoading || (!retailer?.has_free_shipping && shippingCost === null)}
                                    onClick={handleSubmit}
                                >
                                    {t('cart.submit')}
                                </Button>
                            </div>

                            <p className="text-[14px] text-dark-blue/40 text-center">
                                {t('cart.contact')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <ProductModal
                product={modalProduct}
                isOpen={!!modalProduct}
                onClose={() => {
                    setModalProduct(null)
                    setModalVariantId(null)
                }}
                initialVariantId={modalVariantId}
            />

            <ResponsiveModal
                isOpen={confirmOpen}
                onClose={() => !submitting && setConfirmOpen(false)}
                desktopClassName="w-[480px]"
            >
                <div className="p-[32px] flex flex-col gap-[24px]">
                    <h2 className="text-[20px] font-semibold uppercase">
                        {t('cart.confirmTitle')}
                    </h2>

                    <div className="flex flex-col gap-[12px]">
                        <div className="flex justify-between items-center">
                            <span className="text-[16px]">{t('cart.subtotal')}</span>
                            <span className="text-[16px]">{formatPrice(cartSubtotal)}</span>
                        </div>
                        {shippingAmount > 0 && (
                            <div className="flex justify-between items-center">
                                <span className="text-[16px]">{t('cart.shipping')}</span>
                                <span className="text-[16px]">{formatPrice(shippingAmount)}</span>
                            </div>
                        )}
                        {retailer?.has_free_shipping && (
                            <div className="flex justify-between items-center">
                                <span className="text-[16px]">{t('cart.shipping')}</span>
                                <span className="text-[16px]">{t('cart.freeShipping')}</span>
                            </div>
                        )}
                        {hasVat && (
                            <div className="flex justify-between items-center">
                                <span className="text-[16px]">{t('cart.vat')}</span>
                                <span className="text-[16px]">{formatPrice(vatAmount)}</span>
                            </div>
                        )}
                        <div className="border-t border-dark-blue/10" />
                        <div className="flex justify-between items-center">
                            <span className="text-[16px] font-semibold">
                                {hasVat ? t('cart.totalTTC') : t('cart.total')}
                            </span>
                            <span className="text-[16px] font-semibold">
                                {formatPrice(cartTotal)}
                            </span>
                        </div>
                    </div>

                    <p className="text-[14px] text-dark-blue/40">
                        {t('cart.confirmDelay')}
                    </p>

                    {error && (
                        <p className="text-red-500 text-[13px]">{error}</p>
                    )}

                    <div className="flex gap-[12px]">
                        <Button
                            fullWidth
                            variant="outline"
                            disabled={submitting}
                            onClick={() => setConfirmOpen(false)}
                        >
                            {t('cart.confirmCancel')}
                        </Button>
                        <Button
                            fullWidth
                            loading={submitting}
                            onClick={handleConfirm}
                        >
                            {t('cart.confirmSubmit')}
                        </Button>
                    </div>
                </div>
            </ResponsiveModal>
        </div>
    )
}

export default Cart
